import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import "./moreFilter.css";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";

const DEFAULT_FILTERS = {
  colorSelect: "",
  carDriveType: "",
  carEngine: "",
  carFilterAccessories: [],
  carFuelType: "",
  carMileage: "",
  carPrice: "",
  carSeat: "",
  carSteering: "",
  carTransmission: "",
  categoryName: "",
  brandName: "",
  modelNames: [],
  years: [],
};

const COLOR_MAP = {
  WHITE: "#FFFFFF",
  PERL: "#EAE0C8",
  BEIGE: "#F5F5DC",
  SILVER: "#C0C0C0",
  GRAY: "#808080",
  GUNMETAL: "#2a3439",
  BLACK: "#000000",
  PINK: "#FF1493",
  PURPLE: "#9932CC",
  WINE_RED: "#800000",
  RED: "#FF0000",
  YELLOW: "#FFD700",
  ORANGE: "#FFA500",
  GOLD: "#FFD700",
  BROWN: "#8B4513",
  LIGHT_BLUE: "#87CEEB",
  BLUE: "#0000FF",
  DARK_BLUE: "#00008B",
  GREEN: "#32CD32",
  MAZIORA: "#3B3B6D",
  OTHER: "#808080"
};

const MoreFilterDialog = ({
  open,
  onClose,
  onApplyFilters,
  initialFilters = {},
  currentData = { data: [], totalItems: 0 }
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize selectedCategory from initialFilters
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (initialFilters.selectedCategory) {
      return initialFilters.selectedCategory;
    }
    if (initialFilters.categoryName) {
      return { label: initialFilters.categoryName };
    }
    return null;
  });

  // Initialize selectedBrand from initialFilters
  const [selectedBrand, setSelectedBrand] = useState(() => {
    if (initialFilters.selectedBrand) {
      return initialFilters.selectedBrand;
    }
    if (initialFilters.brandName) {
      return { label: initialFilters.brandName };
    }
    return null;
  });

  // Initialize selectedModels from initialFilters
  const [selectedModels, setSelectedModels] = useState(() => {
    const models = initialFilters.selectedModels || initialFilters.modelNames || [];
    if (Array.isArray(models)) {
      return models.map(m => 
        typeof m === 'object' ? m : { carModelName: m }
      ).filter(m => m.carModelName);
    }
    return [];
  });

  // Initialize selectedYears from initialFilters
  const [selectedYears, setSelectedYears] = useState(() => {
    const years = initialFilters.selectedYears || initialFilters.years || [];
    if (Array.isArray(years)) {
      return years
        .map(y => typeof y === 'object' ? y : { year: parseInt(y) })
        .filter(y => !isNaN(y.year));
    }
    return [];
  });

  // Sync state when dialog opens or initialFilters changes
  useEffect(() => {
    if (open) {
      const category = initialFilters.selectedCategory || 
        (initialFilters.categoryName ? { label: initialFilters.categoryName } : null);
      
      const brand = initialFilters.selectedBrand || 
        (initialFilters.brandName ? { label: initialFilters.brandName } : null);
      
      const models = (initialFilters.selectedModels || initialFilters.modelNames || [])
        .map(m => typeof m === 'object' ? m : { carModelName: m })
        .filter(m => m.carModelName);
      
      const years = (initialFilters.selectedYears || initialFilters.years || [])
        .map(y => typeof y === 'object' ? y : { year: parseInt(y) })
        .filter(y => !isNaN(y.year));

      setSelectedCategory(category);
      setSelectedBrand(brand);
      setSelectedModels(models);
      setSelectedYears(years);
    }
  }, [open, JSON.stringify(initialFilters)]);

  // DROPDOWN OPEN/CLOSE STATES
  const [isBodyTypeOpen, setIsBodyTypeOpen] = useState(false);
  const [isBrandOpen, setIsBrandOpen] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);

  const [bodyTypes, setBodyTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);

  const [filters, setFilters] = useState(() => { 
    const initial = { 
      ...DEFAULT_FILTERS, 
      ...initialFilters 
    };
    
    return {
      ...initial,
      modelNames: Array.isArray(initial.modelNames) ? initial.modelNames : [],
      years: Array.isArray(initial.years) ? initial.years : [],
      carFilterAccessories: Array.isArray(initial.carFilterAccessories) ? initial.carFilterAccessories : []
    };
  });

  const [allOptions, setAllOptions] = useState({
    colors: [], driveTypes: [], engines: [], accessories: [], fuelTypes: [], 
    mileages: [], prices: [], seats: [], steerings: [], transmissions: []
  });
  
  const [loading, setLoading] = useState(false);
  const [filteredTotalItems, setFilteredTotalItems] = useState(
    currentData?.totalItems || 0
  );

  const debounceRef = useRef(null);
  const overlayRef = useRef(null);

  // ===== UTILITY FUNCTIONS =====
  const formatBrandName = (brandName) => { 
    if (!brandName) return ""; 
    return brandName.trim().toUpperCase().replace(/\s+/g, ''); 
  };
  
  const formatModelName = (modelName) => { 
    if (!modelName) return ""; 
    return modelName.trim(); 
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = { 
      SEDAN: "/BrandIcons/body1.svg", 
      SUV: "/BrandIcons/body2.svg", 
      WELFARE: "/BrandIcons/body2.svg", 
      HATCHBACK: "/BrandIcons/body3.svg", 
      VAN: "/BrandIcons/body4.svg", 
      CAB_VAN: "/BrandIcons/body4.svg", 
      CONVERTIBLE: "/BrandIcons/body5.svg", 
      COUPE: "/BrandIcons/body6.svg", 
      WAGON: "/BrandIcons/body6.svg", 
      BUS: "/BrandIcons/body6.svg", 
      TRUCK: "/BrandIcons/body7.svg", 
      DUMP: "/BrandIcons/body7.svg", 
      PICK_UP_TRUCK: "/BrandIcons/body7.svg", 
      MINI_VAN: "/BrandIcons/body4.svg", 
      NO_CATEGORIES_NAME: "/BrandIcons/body6.svg", 
      OPEN: "/BrandIcons/body5.svg" 
    };
    return iconMap[categoryName.toUpperCase()] || "/BrandIcons/default.svg";
  };

  const getBrandImage = (brandName) => {
    const imageMap = {
      "ALFAROMEO": "/BrandIcons/Brand_Alfa_Romeo.svg",
      "ALPINA": "/BrandIcons/Brand_Alpina.svg",
      "AUDI": "/BrandIcons/Brand_Audi.svg",
      "BMW": "/BrandIcons/Brand_BMW.svg",
      "FORD": "/BrandIcons/Brand_Ford.svg",
      "HONDA": "/BrandIcons/Brand_Honda.svg",
      "TOYOTA": "/BrandIcons/Brand_Toyota.svg",
      "NISSAN": "/BrandIcons/Brand_Nissan.svg",
    };
    return imageMap[brandName] || imageMap[formatBrandName(brandName)] || "/BrandIcons/default.svg";
  };

  // ===== FETCH FUNCTIONS =====
  const fetchAllCategories = useCallback(async () => {
    try {
      const response = await fetch("https://car.amar.co.jp/api/master/car-catgories/get-all-categories", { 
        headers: { Accept: "application/json", "Accept-Language": "en" } 
      });
      const result = await response.json();
      if (result.status === 200 && result.data) {
        const mappedBodyTypes = result.data.map((category) => ({
          label: category.categoriesName.trim() === "NO_CATEGORIES_NAME" ? "CONVERTIBLE" : category.categoriesName.trim(),
          src: getCategoryIcon(category.categoriesName.trim()),
        }));
        setBodyTypes(mappedBodyTypes);
      }
    } catch (error) { 
      console.error("Error fetching categories:", error); 
    }
  }, []);

  const fetchAllBrands = useCallback(async () => {
    try {
      if (selectedCategory) {
        const queryParams = { 
          categoryName: selectedCategory.label, 
          page: 0, 
          size: 100 
        };
        const response = await axios.get('https://car.amar.co.jp/api/dashboard/filter/advance-filter-new', { 
          params: queryParams, 
          headers: { Accept: "application/json", "Accept-Language": "en" } 
        });
        const data = response.data;
        if (data.data && Array.isArray(data.data)) {
          const uniqueBrands = Array.from(
            new Set(data.data.map(item => item.carBrandName).filter(Boolean))
          ).map(brandName => ({ carBrandName: formatBrandName(brandName) }));
          setBrands(uniqueBrands);
        } else {
          setBrands([]);
        }
      } else {
        const response = await axios.get("https://car.amar.co.jp/api/master/car-brand/get-all-brandName", { 
          headers: { accept: "application/json", "Accept-Language": "en" } 
        });
        if (response.data.carBrandList) { 
          setBrands(response.data.carBrandList); 
        } else {
          setBrands([]);
        }
      }
    } catch (error) { 
      console.error("Error fetching brands:", error); 
    }
  }, [selectedCategory]);

  const fetchAllModels = useCallback(async () => {
    try {
      const categoryName = selectedCategory?.label || "";
      const brandName = selectedBrand?.label || "";

      const params = { 
        categoryName: categoryName, 
        brandName: brandName, 
        page: '0', 
        size: '100' 
      };
      
      const response = await axios.get('https://car.amar.co.jp/api/dashboard/filter/advance-filter-new', { 
        params: params, 
        headers: { Accept: "application/json", "Accept-Language": "en" } 
      });
      
      const data = response.data;
      if (data.data && Array.isArray(data.data)) {
        const uniqueModels = Array.from(
          new Set(data.data.map(item => item.carModelName).filter(Boolean))
        ).map(modelName => ({ carModelName: formatModelName(modelName) }));
        setModels(uniqueModels);
      } else { 
        setModels([]);
      }
    } catch (error) { 
      console.error("Error fetching models:", error); 
      setModels([]);
    }
  }, [selectedCategory, selectedBrand]);

  const fetchAllYears = useCallback(async () => {
    try {
      const hasFilters = selectedCategory || selectedBrand || selectedModels.length > 0;
      if (hasFilters) {
        const params = { 
          categoryName: selectedCategory?.label || "", 
          brandName: selectedBrand?.label || "", 
          modelNames: selectedModels.map(model => model.carModelName), 
          page: '0', 
          size: '100' 
        };
        const response = await axios.get('https://car.amar.co.jp/api/dashboard/filter/advance-filter-new', { 
          params: params, 
          headers: { Accept: "application/json", "Accept-Language": "en" }, 
          paramsSerializer: p => qs.stringify(p, { arrayFormat: 'repeat' }) 
        });
        const data = response.data;
        if (data.data && Array.isArray(data.data)) {
          const yearList = Array.from(
            new Set(data.data.map(item => item.year).filter(Boolean))
          ).map(year => ({ year: parseInt(year) })).sort((a, b) => b.year - a.year);
          setYears(yearList);
        } else {
           const currentYear = new Date().getFullYear();
           const initialYears = Array.from({ length: 16 }, (_, i) => ({ year: currentYear - i }));
           setYears(initialYears);
        }
      } else {
        const currentYear = new Date().getFullYear();
        const initialYears = Array.from({ length: 16 }, (_, i) => ({ year: currentYear - i }));
        setYears(initialYears);
      }
    } catch (error) { 
      console.error("Error fetching years:", error); 
    }
  }, [selectedCategory, selectedBrand, selectedModels]);

  // Initialize data on dialog open
  useEffect(() => {
    if (open) {
      fetchAllCategories();
      fetchAllBrands();
      fetchAllModels();
      fetchAllYears();
      fetchAllOptions();
    }
  }, [open]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleAccessoryToggle = (key) => {
    setFilters((prev) => ({
      ...prev,
      carFilterAccessories: Array.isArray(prev.carFilterAccessories) 
        ? (prev.carFilterAccessories.includes(key)
            ? prev.carFilterAccessories.filter((i) => i !== key)
            : [...prev.carFilterAccessories, key])
        : [key] 
    }));
  };

  const handleColorSelect = (color) => {
    setFilters((prev) => ({ 
      ...prev, 
      colorSelect: prev.colorSelect === color ? "" : color 
    }));
  };

  const resetFilters = () => {
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedModels([]);
    setSelectedYears([]);
    
    setFilters((prev) => ({
      ...prev,
      colorSelect: '',
      carDriveType: '',
      carEngine: '',
      carFilterAccessories: [],
      carFuelType: '',
      carMileage: '',
      carPrice: '',
      carSeat: '',
      carSteering: '',
      carTransmission: ''
    }));
    
    setFilteredTotalItems(currentData?.totalItems || 0);
  };

  const getSelectedCount = () => {
    let count = 0;
    
    if (selectedCategory) count++;
    if (selectedBrand) count++;
    if (selectedModels.length > 0) count += selectedModels.length;
    if (selectedYears.length > 0) count += selectedYears.length;

    [
      "colorSelect", "carDriveType", "carEngine", "carFuelType", 
      "carMileage", "carPrice", "carSeat", "carSteering", "carTransmission"
    ].forEach((k) => {
      if (filters[k] && `${filters[k]}`.trim() !== "") count++;
    });

    if (Array.isArray(filters.carFilterAccessories)) {
      count += filters.carFilterAccessories.filter(Boolean).length;
    }
    
    return count;
  };

  const buildApiParams = useCallback((baseFilters) => {
    const p = { ...baseFilters };
    Object.keys(p).forEach((k) => {
      const v = p[k];
      if (v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) delete p[k];
    });
    return p;
  }, []);

  const handleShowCars = useCallback(() => {
    const navParams = buildApiParams({
      categoryName: selectedCategory?.label || '',
      brandName: selectedBrand?.label || '',
      modelNames: selectedModels.map(m => m.carModelName),
      years: selectedYears.map(y => y.year),
      
      colorSelect: filters.colorSelect, 
      carDriveType: filters.carDriveType, 
      carEngine: filters.carEngine,
      carFilterAccessories: Array.isArray(filters.carFilterAccessories) ? filters.carFilterAccessories : [], 
      carFuelType: filters.carFuelType,
      carMileage: filters.carMileage, 
      carPrice: filters.carPrice, 
      carSeat: filters.carSeat,
      carSteering: filters.carSteering, 
      carTransmission: filters.carTransmission,
      
      page: 1, 
      size: 20,
    });

    const completeFilterState = {
      selectedCategory: selectedCategory,
      selectedBrand: selectedBrand,
      selectedModels: selectedModels,
      selectedYears: selectedYears,
      
      categoryName: selectedCategory?.label || "",
      brandName: selectedBrand?.label || "",
      modelNames: selectedModels.map(m => m.carModelName),
      years: selectedYears.map(y => y.year),
      
      colorSelect: filters.colorSelect || "", 
      carDriveType: filters.carDriveType || "", 
      carEngine: filters.carEngine || "",
      carFilterAccessories: Array.isArray(filters.carFilterAccessories) ? filters.carFilterAccessories : [], 
      carFuelType: filters.carFuelType || "",
      carMileage: filters.carMileage || "", 
      carPrice: filters.carPrice || "", 
      carSeat: filters.carSeat || "",
      carSteering: filters.carSteering || "", 
      carTransmission: filters.carTransmission || "",
    };
    
    const searchString = qs.stringify(navParams, { arrayFormat: "repeat", encode: false });

    navigate(
      { 
        pathname: "/FilterByGlobalSearch", 
        search: `?${searchString}` 
      },
      { 
        state: { 
          filterState: completeFilterState, 
          totalItems: filteredTotalItems, 
          key: Date.now(),
        } 
      }
    );

    onApplyFilters?.(completeFilterState);
    onClose?.();
  }, [filters, selectedCategory, selectedBrand, selectedModels, selectedYears, buildApiParams, navigate, filteredTotalItems, onClose, onApplyFilters]);

  const extractValueFromObject = (item, keyPatterns) => {
    if (typeof item === 'string') return item;
    if (typeof item !== 'object' || item === null) return String(item);
    
    for (const pattern of keyPatterns) {
      if (item[pattern] !== undefined) { return item[pattern]; }
    }
    for (const key in item) {
      if (typeof item[key] === 'string') { return item[key]; }
    }
    return String(item);
  };

  const fetchAllOptions = useCallback(async () => {
    setLoading(true);
    try {
      const endpoints = [
        { url: 'https://car.amar.co.jp/api/dashboard/car-color/get-all-color', key: 'colors', extractPatterns: ['carColorName', 'carColor', 'colorName', 'color'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-drive-type/get-all-drive-type', key: 'driveTypes', extractPatterns: ['carDriveType', 'driveType', 'type'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-engine/get-all-engine-cc-range', key: 'engines', extractPatterns: ['carEngineCCRange', 'engineRange', 'engine'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-filter-accessories/get-all-filter-accessories', key: 'accessories', extractPatterns: ['carFilterAccessories', 'accessory', 'name'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-fuel-type/get-all-fuel-type', key: 'fuelTypes', extractPatterns: ['carFuelType', 'fuelType', 'type'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-mileage/get-all-mileage-range', key: 'mileages', extractPatterns: ['carMileageRange', 'mileageRange', 'mileage'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-price/get-all-price-range', key: 'prices', extractPatterns: ['carPriceRange', 'priceRange', 'price'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-seat/get-all-seat', key: 'seats', extractPatterns: ['carSeat', 'seat', 'seats'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-steering/get-all-steering-type', key: 'steerings', extractPatterns: ['carSteering', 'steering', 'type'] },
        { url: 'https://car.amar.co.jp/api/dashboard/car-transmission/get-all-transmission-type', key: 'transmissions', extractPatterns: ['carTransmission', 'transmission', 'type'] }
      ];

      const requests = endpoints.map(endpoint => 
        axios.get(endpoint.url, { headers: { 'Accept-Language': 'en', 'accept': 'application/json' } })
        .then(response => ({ key: endpoint.key, data: response.data, extractPatterns: endpoint.extractPatterns }))
      );

      const results = await Promise.all(requests);
      
      const options = {};
      results.forEach(result => {
        let dataArray = (result.data && result.data.data) || (Array.isArray(result.data) ? result.data : []);
        options[result.key] = dataArray.map(item => extractValueFromObject(item, result.extractPatterns));
      });
      
      if (options.accessories && Array.isArray(options.accessories)) {
        options.accessories = options.accessories.map(acc => ({
          carFilterAccessories: acc,
          displayName: typeof acc === 'string' ? acc.replace(/_/g, ' ').toUpperCase().replace(/\b\w/g, l => l.toUpperCase()) : String(acc)
        }));
      }
      
      if (options.colors && Array.isArray(options.colors)) {
        options.colors = options.colors.map(color => ({
          carColor: color,
          displayName: typeof color === 'string' ? color.charAt(0) + color.slice(1).toLowerCase() : String(color)
        }));
      }
      
      setAllOptions(options);
    } catch (error) {
      console.error('Error fetching options:', error);
      setAllOptions(prev => ({ ...prev, colors: [], driveTypes: [], engines: [], accessories: [], fuelTypes: [], mileages: [], prices: [], seats: [], steerings: [], transmissions: [] }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced filter count update
  useEffect(() => {
    if (!open) return;
    
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const apiParams = {
          categoryName: selectedCategory?.label || '',
          brandName: selectedBrand?.label || '',
          modelNames: selectedModels.map(m => m.carModelName),
          years: selectedYears.map(y => y.year),
          colorSelect: filters.colorSelect, 
          carDriveType: filters.carDriveType, 
          carEngine: filters.carEngine,
          carFilterAccessories: Array.isArray(filters.carFilterAccessories) ? filters.carFilterAccessories : [], 
          carFuelType: filters.carFuelType,
          carMileage: filters.carMileage, 
          carPrice: filters.carPrice, 
          carSeat: filters.carSeat,
          carSteering: filters.carSteering, 
          carTransmission: filters.carTransmission,
          page: 0,
          size: 1,
        };

        Object.keys(apiParams).forEach(key => {
          const value = apiParams[key];
          if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
            delete apiParams[key];
          }
        });

        const res = await axios.get(
          "https://car.amar.co.jp/api/dashboard/filter/advance-filter-new",
          {
            params: apiParams,
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
            headers: { "Accept-Language": "en", accept: "application/json" },
          }
        );

        setFilteredTotalItems(res.data?.totalItems || res.data?.pageInfo?.totalItems || 0);
      } catch (err) {
        console.error("Error fetching filtered count:", err);
        setFilteredTotalItems(0);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [open, selectedCategory, selectedBrand, selectedModels, selectedYears, filters]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="mfd-overlay" ref={overlayRef} role="presentation" onClick={onClose}>
      <div 
        className="mfd-modal" 
        role="dialog" 
        aria-modal="true" 
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="mfd-close" 
          aria-label="Close filters" 
          onClick={onClose}
        >
          <CloseIcon fontSize="medium" />
        </button>

        {/* Top Selection Row: Body Type, Brand, Models, Years */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
          padding: "20px 0px",
          marginBottom: "10px"
        }}>
          {/* Body Type Custom Dropdown */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', fontSize: '12px' }}>Body Type</label>
            <button
              onClick={() => setIsBodyTypeOpen(!isBodyTypeOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {selectedCategory?.label || 'Select Body Type'}
            </button>
            {isBodyTypeOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {bodyTypes.map((type, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedCategory(type);
                      setIsBodyTypeOpen(false);
                      setSelectedBrand(null);
                      setSelectedModels([]);
                      setSelectedYears([]);
                      fetchAllBrands();
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      backgroundColor: selectedCategory?.label === type.label ? '#f0f0f0' : '#fff',
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = selectedCategory?.label === type.label ? '#f0f0f0' : '#fff'}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="img" src={type.src} alt={type.label} sx={{ width: 18, height: 18 }} />
                      <span>{type.label}</span>
                    </Box>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Brand Custom Dropdown */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', fontSize: '12px' }}>Brand</label>
            <button
              onClick={() => setIsBrandOpen(!isBrandOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {selectedBrand?.label || 'Select Brand'}
            </button>
            {isBrandOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {brands.map((brand, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedBrand({ label: brand.carBrandName });
                      setIsBrandOpen(false);
                      setSelectedModels([]);
                      setSelectedYears([]);
                      fetchAllModels();
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      backgroundColor: selectedBrand?.label === brand.carBrandName ? '#f0f0f0' : '#fff',
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = selectedBrand?.label === brand.carBrandName ? '#f0f0f0' : '#fff'}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="img" src={getBrandImage(brand.carBrandName)} alt={brand.carBrandName} sx={{ width: 18, height: 18 }} />
                      <span>{brand.carBrandName}</span>
                    </Box>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Models Custom Dropdown */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', fontSize: '12px' }}>Models</label>
            <button
              onClick={() => setIsModelOpen(!isModelOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {selectedModels.length === 0 ? 'Select Models' : `${selectedModels.length} selected`}
            </button>
            {isModelOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {models.map((model, idx) => (
                  <label
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedModels.some(m => m.carModelName === model.carModelName)}
                      onChange={() => {
                        const isSelected = selectedModels.some(m => m.carModelName === model.carModelName);
                        if (isSelected) {
                          setSelectedModels(selectedModels.filter(m => m.carModelName !== model.carModelName));
                        } else {
                          setSelectedModels([...selectedModels, model]);
                        }
                      }}
                    />
                    <span>{model.carModelName}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Years Custom Dropdown */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block', fontSize: '12px' }}>Years</label>
            <button
              onClick={() => setIsYearOpen(!isYearOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {selectedYears.length === 0 ? 'Select Years' : `${selectedYears.length} selected`}
            </button>
            {isYearOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '5px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {years.map((year, idx) => (
                  <label
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedYears.some(y => y.year === year.year)}
                      onChange={() => {
                        const isSelected = selectedYears.some(y => y.year === year.year);
                        if (isSelected) {
                          setSelectedYears(selectedYears.filter(y => y.year !== year.year));
                        } else {
                          setSelectedYears([...selectedYears, year]);
                        }
                      }}
                    />
                    <span>{year.year}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rest of filters sections */}
        <div className="mfd-grid">
          {allOptions.mileages.length > 0 && (
            <div className="mfd-section">
              <label className="mfd-label">Mileage</label>
              <select 
                className="mfd-select" 
                value={filters.carMileage} 
                onChange={(e) => handleFilterChange("carMileage", e.target.value)}
              >
                <option value="">Select Mileage</option>
                {allOptions.mileages.map((m, i) => (
                  <option key={i} value={m}>{m}</option>
                ))}
              </select>
            </div>
          )}

          {allOptions.engines.length > 0 && (
            <div className="mfd-section">
              <label className="mfd-label">Engine</label>
              <select 
                className="mfd-select" 
                value={filters.carEngine} 
                onChange={(e) => handleFilterChange("carEngine", e.target.value)}
              >
                <option value="">Select Engine</option>
                {allOptions.engines.map((eng, i) => (
                  <option key={i} value={eng}>{eng}</option>
                ))}
              </select>
            </div>
          )}

          {allOptions.seats.length > 0 && (
            <div className="mfd-section">
              <label className="mfd-label">Seats</label>
              <select 
                className="mfd-select" 
                value={filters.carSeat} 
                onChange={(e) => handleFilterChange("carSeat", e.target.value)}
              >
                <option value="">Select Seats</option>
                {allOptions.seats.map((s, i) => (
                  <option key={i} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {allOptions.driveTypes.length > 0 && (
            <div className="mfd-section">
              <label className="mfd-label">Drive Type</label>
              <select 
                className="mfd-select" 
                value={filters.carDriveType} 
                onChange={(e) => handleFilterChange("carDriveType", e.target.value)}
              >
                <option value="">Select Drive Type</option>
                {allOptions.driveTypes.map((d, i) => (
                  <option key={i} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {allOptions.fuelTypes.length > 0 && (
            <div className="mfd-section">
              <label className="mfd-label">Fuel Type</label>
              <select 
                className="mfd-select" 
                value={filters.carFuelType} 
                onChange={(e) => handleFilterChange("carFuelType", e.target.value)}
              >
                <option value="">Select Fuel Type</option>
                {allOptions.fuelTypes.map((f, i) => (
                  <option key={i} value={f}>{f}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mfd-grid">
          {allOptions.colors.length > 0 && (
            <div>
              <h3 className="mfd-heading">Popular Color</h3>
              <div className="mfd-scroll">
                <div className="mfd-colors-grid">
                  {allOptions.colors.map((c) => {
                    const clr = COLOR_MAP[c.carColor] || "#CCCCCC";
                    const selected = filters.colorSelect === c.carColor;
                    return (
                      <div key={c.carColor} className="mfd-color-wrap">
                        <button
                          type="button"
                          className={`mfd-color-btn ${selected ? "selected" : ""}`}
                          onClick={() => handleColorSelect(c.carColor)}
                          style={{ 
                            backgroundColor: clr, 
                            border: c.carColor === "WHITE" && !selected ? "1px solid #ddd" : undefined 
                          }}
                          title={c.displayName}
                        />
                        <div className="mfd-color-name">{c.displayName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {allOptions.accessories.length > 0 && (
            <div>
              <h3 className="mfd-heading">Accessories</h3>
              <div className="mfd-scroll accessory-list">
                {allOptions.accessories.map((a) => (
                  <label key={a.carFilterAccessories} className="mfd-checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={Array.isArray(filters.carFilterAccessories) 
                        ? filters.carFilterAccessories.includes(a.carFilterAccessories)
                        : false
                      } 
                      onChange={() => handleAccessoryToggle(a.carFilterAccessories)} 
                    />
                    <span>{a.displayName}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {allOptions.transmissions.length > 0 && (
            <div>
              <h3 className="mfd-heading">Transmission</h3>
              <div>
                {allOptions.transmissions.map((t, i) => (
                  <label key={i} className="mfd-radio-label">
                    <input 
                      type="radio" 
                      name="transmission" 
                      value={t} 
                      checked={filters.carTransmission === t} 
                      onChange={(e) => handleFilterChange("carTransmission", e.target.value)} 
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {allOptions.steerings.length > 0 && (
            <div>
              <h3 className="mfd-heading">Steering</h3>
              <div>
                {allOptions.steerings.map((s, i) => (
                  <label key={i} className="mfd-radio-label">
                    <input 
                      type="radio" 
                      name="steering" 
                      value={s} 
                      checked={filters.carSteering === s} 
                      onChange={(e) => handleFilterChange("carSteering", e.target.value)} 
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {allOptions.prices.length > 0 && (
            <div>
              <h3 className="mfd-heading">Price Range</h3>
              <select 
                className="mfd-select" 
                value={filters.carPrice} 
                onChange={(e) => handleFilterChange("carPrice", e.target.value)}
              >
                <option value="">Select Price Range</option>
                {allOptions.prices.map((p, i) => (
                  <option key={i} value={p}>{p}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mfd-footer">
          <div className="mfd-left">
            <div className="mfd-selected-label">Total Filters Selected</div>
            <div className="mfd-selected-count">{getSelectedCount()}</div>
            <button className="mfd-reset-btn" onClick={resetFilters}>
              RESET FILTERS
            </button>
          </div>

          <div>
            <button className="mfd-show-btn" onClick={handleShowCars}>
              SHOW: {filteredTotalItems?.toLocaleString?.() ?? filteredTotalItems ?? 0} CARS
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MoreFilterDialog;